/**
 * shared/nguonSo.js : nhãn nguồn và độ mới cho từng khối số trong hồ sơ gửi
 * nhà đầu tư.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO CÓ NHÃN NÀY
 *
 * Nhà đầu tư đọc một con số sẽ hỏi hai câu: số này lấy từ đâu, và tính lúc
 * nào. Bản một trang trước đây chỉ có một dòng "doanh nghiệp tự khai" cho cả
 * tài liệu, nên runway tính từ sao kê ngân hàng tuần trước trông y hệt runway
 * ước đoán từ năm tháng trước.
 *
 * CHỈ HAI MỨC, CỐ Ý:
 *  - 'tu-tinh'     : founder tự nhập số vào công cụ (mặc định).
 *  - 'co-tai-lieu' : founder cho biết có tài liệu đối chiếu (sao kê, báo cáo
 *                    tài chính, hợp đồng...) và sẽ gửi khi nhà đầu tư hỏi.
 *
 * fundraising.vn KHÔNG xem, KHÔNG lưu và KHÔNG xác nhận tài liệu đó. Không có
 * mức "đã xác minh": muốn có mức ấy thì phải có người đọc sao kê của người
 * khác và chịu trách nhiệm nếu đọc sai. Câu chữ hiển thị phải giữ đúng ranh
 * giới này: "doanh nghiệp cho biết có tài liệu", không phải "có tài liệu".
 *
 * Dữ liệu nằm ở profile.nguonSo = { [toolId]: { loai, taiLieu } }, đi theo cơ
 * chế gộp từng ô của hồ sơ (profileMoc), nên sửa trên điện thoại không bị bản
 * cũ trên máy tính đè lại. Không lưu ngày cập nhật ở đây: ngày lấy từ
 * drafts[toolId].updated, tức lần cuối founder lưu kết quả công cụ.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const LOAI_NGUON = ['tu-tinh', 'co-tai-lieu'];

/** Tên tài liệu chỉ cần đủ để nhà đầu tư biết hỏi xin cái gì. */
export const TRAN_TEN_TAI_LIEU = 80;

/** Số tính quá chừng này ngày thì nhắc founder chạy lại trước khi gửi. */
export const SO_NGAY_CU = 90;

const plain = (x) => x !== null && typeof x === 'object' && !Array.isArray(x);

/**
 * profile.nguonSo có hợp lệ không. idHopLe: tập toolId được phép (tránh khoá
 * lạ phình dữ liệu đồng bộ).
 */
export function nguonSoHopLe(x, idHopLe) {
  if (x === undefined) return true;
  if (!plain(x)) return false;
  const muc = Object.entries(x);
  if (muc.length > 40) return false;
  return muc.every(([id, v]) => {
    if (idHopLe && !idHopLe.has(id)) return false;
    if (!plain(v)) return false;
    if (!LOAI_NGUON.includes(v.loai)) return false;
    if (v.taiLieu !== undefined
      && (typeof v.taiLieu !== 'string' || v.taiLieu.length > TRAN_TEN_TAI_LIEU)) return false;
    return true;
  });
}

/** Ngày dạng 01/11/2026, không phụ thuộc ngôn ngữ của máy. */
export function ngayVN(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hai = (n) => String(n).padStart(2, '0');
  return `${hai(d.getDate())}/${hai(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Số ngày từ lúc cập nhật tới hôm nay; không có ngày thì null. */
export function soNgayTu(iso, homNay = new Date()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((homNay.getTime() - t) / 86400000));
}

/**
 * Nhãn cho một khối số.
 * @param {{loai?: string, taiLieu?: string}|undefined} nguon profile.nguonSo[toolId]
 * @param {string} capNhat drafts[toolId].updated
 * @returns {{loai: string, taiLieu: string, ngay: string, soNgay: number|null, cu: boolean, chu: string}}
 */
export function nhanNguon(nguon, capNhat, homNay = new Date()) {
  const taiLieu = String(nguon?.taiLieu || '').trim().slice(0, TRAN_TEN_TAI_LIEU);
  // Chọn "có tài liệu" mà không ghi tên tài liệu thì nhà đầu tư không biết hỏi
  // xin cái gì: coi như tự tính, đừng để một lời khẳng định rỗng đi ra ngoài.
  const loai = nguon?.loai === 'co-tai-lieu' && taiLieu ? 'co-tai-lieu' : 'tu-tinh';
  const ngay = ngayVN(capNhat);
  const soNgay = soNgayTu(capNhat, homNay);
  const cu = soNgay !== null && soNgay > SO_NGAY_CU;
  const dau = loai === 'co-tai-lieu'
    ? `Doanh nghiệp cho biết có tài liệu đối chiếu: ${taiLieu}`
    : 'Doanh nghiệp tự tính';
  const chu = ngay ? `${dau}. Số liệu ngày ${ngay}.` : `${dau}.`;
  return { loai, taiLieu: loai === 'co-tai-lieu' ? taiLieu : '', ngay, soNgay, cu, chu };
}

/** Câu chân trang, chỉ cần khi có ít nhất một khối "có tài liệu". */
export const GIAI_THICH_TAI_LIEU = 'Với khối ghi "có tài liệu đối chiếu", doanh nghiệp sẽ gửi tài liệu '
  + 'khi người nhận yêu cầu. Fundraising.vn không xem, không lưu và không xác nhận các tài liệu này.';
